import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Download, ArrowLeft, Mail, Phone, MapPin, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'

const PDF_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_global-ops-6/artifacts/zwbmfczl_Akshay%20Rao%28Help%20Desk%29.PDF.PDF%20%281%29.pdf"
const DOWNLOAD_URL = `${process.env.REACT_APP_BACKEND_URL}/api/resume`

const experience = [
  {
    title: "IT Support / Technology Operations Coordinator",
    company: "Punjab Imports",
    location: "Melbourne, VIC",
    period: "Nov 2025 — Present",
    highlights: [
      "Day-to-day onsite and remote IT support across multiple business locations",
      "Resolve hardware, software, printer, POS, network and user-access issues",
      "Configure computers, POS terminals, barcode scanners, Wi-Fi and VPN equipment",
      "Support new store setups, system changes and ongoing business technology needs",
    ],
  },
  {
    title: "Data Center Operator",
    company: "Khorafi Business Machines — CBK Project",
    location: "Kuwait",
    period: "May 2023 — Aug 2025",
    highlights: [
      "Managed mainframe databases and backup processes for the Central Bank of Kuwait",
      "Monitored backup operations and responded promptly to incidents",
      "Minimised downtime and data loss through proactive troubleshooting",
    ],
  },
  {
    title: "IT Support Representative",
    company: "Bergan General Trading and Contracting",
    location: "Kuwait",
    period: "Aug 2022 — Apr 2023",
    highlights: [
      "Hardware diagnostics, repairs and upgrades including memory, storage and peripherals",
      "Logged and tracked support requests in ticketing system, reducing repeat incidents",
      "Installed, configured and troubleshot Windows 10/11 and Microsoft Office",
    ],
  },
  {
    title: "IT Support Representative",
    company: "Tech Mahindra Business Services",
    location: "Pune, India",
    period: "Sep 2021 — Apr 2022",
    highlights: [
      "Troubleshooting guidance for internet connectivity, router setup and software issues",
      "Assisted customers with installation and configuration of broadband services",
      "Documented interactions and solutions in CRM for accurate tracking",
    ],
  },
]

const skills = [
  "Incident Management & Troubleshooting",
  "Ticketing Systems (BMC Remedy)",
  "User Account Management (Active Directory)",
  "System Monitoring & Error Resolution",
  "Hardware & Software Installation",
  "Network Connectivity & Printer Support",
  "Remote Desktop Support",
  "POS Systems & Barcode Scanners",
]

const education = [
  { degree: "Masters — Applied IT", school: "Victoria University, Melbourne", year: "2024 — Present" },
  { degree: "B.Tech — Information Technology", school: "Ajeenkya DY Patil University", year: "2017 — 2021" },
  { degree: "Dell EMC Python Associate", school: "Certified", year: "2023" },
]

export default function Resume() {
  return (
    <>
      <Helmet>
        <title>Resume — Akshay Rao | IT Support & Implementation Specialist Melbourne</title>
        <meta name="description" content="Resume of Akshay Rao — IT Support & Implementation Specialist based in Melbourne. Data center operator, IT support, POS systems, infrastructure across India, Kuwait & Australia." />
        <link rel="canonical" href="https://akshayrao.com.au/resume" />
        <meta property="og:title" content="Resume — Akshay Rao | IT Support Melbourne" />
        <meta property="og:description" content="IT Support & Implementation Specialist — Data center, helpdesk, POS, infrastructure. Melbourne-based." />
      </Helmet>

      <div className="min-h-screen bg-[#FAFAFA]">

        {/* Top action bar */}
        <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40 pt-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 h-12 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-[#6B7280] text-sm font-body hover:text-[#0047AB] transition-colors">
              <ArrowLeft size={14} /> Back to Portfolio
            </Link>
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="resume-download-btn"
              className="flex items-center gap-2 px-5 py-2 bg-[#0047AB] text-white text-sm font-semibold rounded-lg hover:bg-[#003d94] transition-colors"
            >
              <Download size={13} /> Download PDF
            </a>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left sidebar */}
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-1 space-y-6"
            >
              {/* Name / Title */}
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-card">
                <div className="w-14 h-14 rounded-xl bg-[#0047AB] flex items-center justify-center mb-4">
                  <span className="font-mono text-white font-bold text-lg">AR</span>
                </div>
                <h1 className="font-sans font-bold text-xl text-[#111111] leading-tight mb-1">Akshay Rao</h1>
                <p className="font-body text-[#6B7280] text-sm leading-snug">IT Support & Implementation Specialist</p>
                <div className="mt-4 pt-4 border-t border-[#F3F4F6] space-y-2.5">
                  <a href="tel:+61452340963" className="flex items-center gap-2.5 text-xs font-body text-[#6B7280] hover:text-[#0047AB] transition-colors">
                    <Phone size={12} className="text-[#0047AB] shrink-0" /> +61 452 340 963
                  </a>
                  <a href="mailto:akshayraoisme@gmail.com" className="flex items-center gap-2.5 text-xs font-body text-[#6B7280] hover:text-[#0047AB] transition-colors">
                    <Mail size={12} className="text-[#0047AB] shrink-0" /> akshayraoisme@gmail.com
                  </a>
                  <span className="flex items-center gap-2.5 text-xs font-body text-[#6B7280]">
                    <MapPin size={12} className="text-[#0047AB] shrink-0" /> Melbourne, VIC, Australia
                  </span>
                  <a href="https://akshayrao.com.au" className="flex items-center gap-2.5 text-xs font-body text-[#0047AB] hover:underline transition-colors">
                    <ExternalLink size={12} className="shrink-0" /> akshayrao.com.au
                  </a>
                </div>
              </div>

              {/* Skills */}
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-card">
                <h2 className="font-mono text-[#0047AB] text-[10px] uppercase tracking-[0.25em] mb-4">Skills</h2>
                <div className="space-y-2">
                  {skills.map(skill => (
                    <div key={skill} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#0047AB] mt-1.5 shrink-0" />
                      <span className="font-body text-[#374151] text-xs leading-snug">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Education */}
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-card">
                <h2 className="font-mono text-[#0047AB] text-[10px] uppercase tracking-[0.25em] mb-4">Education</h2>
                <div className="space-y-4">
                  {education.map(e => (
                    <div key={e.degree}>
                      <div className="font-sans font-semibold text-[#111111] text-xs leading-snug">{e.degree}</div>
                      <div className="font-body text-[#6B7280] text-[11px]">{e.school}</div>
                      <div className="font-mono text-[#9CA3AF] text-[10px]">{e.year}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Download CTA */}
              <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 border-2 border-[#0047AB] text-[#0047AB] text-sm font-semibold rounded-xl hover:bg-[#0047AB] hover:text-white transition-all duration-200">
                <Download size={14} /> Download Full PDF
              </a>
            </motion.aside>

            {/* Main content — Experience */}
            <motion.main
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-2"
            >
              {/* Objective */}
              <div className="bg-[#EBF2FF] border border-[#0047AB]/20 rounded-2xl p-6 mb-6">
                <h2 className="font-mono text-[#0047AB] text-[10px] uppercase tracking-[0.25em] mb-2">Objective</h2>
                <p className="font-body text-[#374151] text-sm leading-relaxed">
                  IT support professional seeking an IT Help Desk or End User Support role. Experienced in delivering reliable technical support across hardware, software, network, POS, and data center environments — across India, Kuwait and Australia.
                </p>
              </div>

              {/* Experience */}
              <h2 className="font-mono text-[#0047AB] text-[10px] uppercase tracking-[0.25em] mb-4">Professional Experience</h2>
              <div className="space-y-4">
                {experience.map((role, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.08, duration: 0.4 }}
                    className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-card"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div>
                        <h3 className="font-sans font-bold text-[#111111] text-base leading-tight">{role.title}</h3>
                        <div className="font-body text-[#0047AB] text-sm mt-0.5">{role.company}</div>
                        <div className="font-body text-[#9CA3AF] text-xs mt-0.5">{role.location}</div>
                      </div>
                      <span className="font-mono text-[#9CA3AF] text-xs shrink-0 mt-0.5">{role.period}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {role.highlights.map((h, j) => (
                        <li key={j} className="flex items-start gap-2 text-[#6B7280] text-sm font-body">
                          <span className="w-1 h-1 rounded-full bg-[#0047AB] mt-2 shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

              {/* PDF embed — desktop */}
              <div className="mt-8 hidden md:block">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-mono text-[#0047AB] text-[10px] uppercase tracking-[0.25em]">Original PDF</h2>
                  <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-[#6B7280] font-body hover:text-[#0047AB] transition-colors">
                    <ExternalLink size={11} /> Open in new tab
                  </a>
                </div>
                <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-card bg-white" style={{ height: '700px' }}>
                  <iframe
                    src={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(PDF_URL)}`}
                    title="Akshay Rao Resume PDF"
                    className="w-full h-full"
                    style={{ border: 'none' }}
                  />
                </div>
              </div>
            </motion.main>
          </div>
        </div>
      </div>
    </>
  )
}
